import React, { Component } from "react";
import { connect } from "react-redux";
import { string, func, arrayOf, shape, bool, number } from "prop-types";
import { FilePond, File, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import "filepond/dist/filepond.min.css";

import * as i18n from "util/i18n";
import * as fileMiddleware from "middleware/file";
import * as configUtils from "util/config";
import { selectors as fileSelectors } from "reducers/formFields/multilineFile";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as i18nSelectors } from "reducers/i18n";
import { channel } from "middleware/api";
import { actions as notificationActions } from "reducers/notification";
import { actions as thirdPaymentActions } from "reducers/thirdPayment";
import { isRegExp } from "lodash";

// Register the plugins
registerPlugin(FilePondPluginFileValidateSize, FilePondPluginFileValidateType);

class FileUploader extends Component {
    static propTypes = {
        idForm: string,
        idFormField: string,
        idActivity: string,
        description: string,
        onRemoveFile: func,
        onAddFile: func,
        onError: func,
        onFileProcess: func,
        setValue: func,
        lang: string.isRequired,
        idEnvironment: number.isRequired,
        files: arrayOf(shape({})),
        idRelatedFile: string,
        accessToken: string.isRequired,
        acceptedFileTypes: arrayOf(string),
        successNotificationMessage: string,
        warningNotificationMessage: string,
        errorNotificationMessage: string,
        dispatch: func.isRequired,
        notificationScope: string,
        fileNameRegex: shape({}),
        allowFileTypeValidation: bool,
        fileErrorMessage: string,
        idAccount: string,
    };
    // More props:
    // - https://pqina.nl/filepond/docs/patterns/api/filepond-instance/#properties
    // - https://pqina.nl/filepond/docs/patterns/plugins/file-validate-size/#properties
    // - https://pqina.nl/filepond/docs/patterns/plugins/file-validate-type/#properties
    // - https://pqina.nl/filepond/docs/patterns/plugins/image-preview/#properties

    static defaultProps = {
        idForm: null,
        idFormField: null,
        idActivity: null,
        description: null,
        onRemoveFile: null,
        onAddFile: null,
        setValue: null,
        files: null,
        idRelatedFile: null,
        onError: null,
        onFileProcess: null,
        acceptedFileTypes: [""],
        successNotificationMessage: "",
        warningNotificationMessage: "",
        errorNotificationMessage: "",
        notificationScope: "form",
        fileNameRegex: null,
        allowFileTypeValidation: true,
        fileErrorMessage: "lotPayments.file.error",
        idAccount: "",
    };

    state = {
        files: [],
    };

    shouldComponentUpdate(nextProps) {
        const { files } = this.state;

        return nextProps?.files?.length !== files.length;
    }

    componentWillUnmount() {
        const { setValue } = this.props;
        if (setValue) {
            setValue([]);
        }
    }

    onProcessFile = (error, fileItem) => {
        const { onAddFile } = this.props;

        if (!error && fileItem) {
            const { files } = this.props;
            const file = {
                fileId: parseInt(fileItem.serverId, 10),
                fileName: fileItem.filename,
                fileSize: fileItem.fileSize,
                fileType: fileItem.fileType,
            };

            this.setState({ files: [...(files || []), file] }, () => {
                if (onAddFile) {
                    onAddFile(file);
                }
            });
        }
    };

    onRemoveFile = ({ serverId }) => {
        const { onRemoveFile, idRelatedFile } = this.props;
        const { files } = this.state;

        this.setState({ files: files?.filter((file) => file.fileId !== serverId) }, () => {
            if (onRemoveFile) {
                onRemoveFile(parseInt(serverId, 10));
            }
        });

        fileMiddleware.deleteFile(parseInt(serverId, 10));
        if (idRelatedFile) {
            fileMiddleware.deleteFile(parseInt(idRelatedFile, 10));
        }
        return true;
    };

    getLabels = () => ({
        labelIdle: i18n.get("file.upload.input.labelIdle"),
        labelFileWaitingForSize: i18n.get("file.upload.input.labelFileWaitingForSize"),
        labelFileSizeNotAvailable: i18n.get("file.upload.input.labelFileSizeNotAvailable"),
        labelFileLoading: i18n.get("file.upload.input.labelFileLoading"),
        labelFileLoadError: i18n.get("file.upload.input.labelFileLoadError"),
        labelFileProcessing: i18n.get("file.upload.input.labelFileProcessing"),
        labelFileProcessingComplete: i18n.get("file.upload.input.labelFileProcessingComplete"),
        labelFileProcessingAborted: i18n.get("file.upload.input.labelFileProcessingAborted"),
        labelFileProcessingError: i18n.get("file.upload.input.labelFileProcessingError"),
        labelTapToCancel: i18n.get("file.upload.input.labelTapToCancel"),
        labelTapToRetry: "",
        labelTapToUndo: i18n.get("file.upload.input.labelTapToUndo"),
        labelButtonRemoveItem: i18n.get("file.upload.input.labelButtonRemoveItem"),
        labelButtonAbortItemLoad: i18n.get("file.upload.input.labelButtonAbortItemLoad"),
        labelButtonRetryItemLoad: i18n.get("file.upload.input.labelButtonRetryItemLoad"),
        labelButtonAbortItemProcessing: i18n.get("file.upload.input.labelButtonAbortItemProcessing"),
        labelButtonUndoItemProcessing: i18n.get("file.upload.input.labelButtonUndoItemProcessing"),
        labelButtonRetryItemProcessing: i18n.get("file.upload.input.labelButtonRetryItemProcessing"),
        labelButtonProcessItem: i18n.get("file.upload.input.labelButtonProcessItem"),
        labelMaxFileSizeExceeded: i18n.get("file.upload.input.labelMaxFileSizeExceeded"),
        labelMaxFileSize: i18n.get("file.upload.input.labelMaxFileSize"),
        labelTotalFileSizeExceeded: i18n.get("file.upload.input.labelTotalFileSizeExceeded"),
        labelMaxTotalFileSize: i18n.get("file.upload.input.labelMaxTotalFileSize"),
        labelMinFileSizeExceeded: i18n.get("file.upload.input.labelMinFileSizeExceeded"),
        labelMinTotalFileSize: i18n.get("file.upload.input.labelMinTotalFileSize"),
        labelMinFileSize: i18n.get("file.upload.input.labelMinFileSize"),
        labelFileTypeNotAllowed: i18n.get("file.upload.input.labelFileTypeNotAllowed"),
        fileValidateTypeLabelExpectedTypes: i18n.get("file.upload.input.fileValidateTypeLabelExpectedTypes"),
    });

    // TODO: Pending migrate this logic to use our middleware (Axios)
    getServerOptions = () => ({
        load: null,
        fetch: null,
        restore: null,
        process: async (fieldName, file, metadata, load, error, progress, abort) => {
            const {
                idForm,
                idFormField,
                idActivity,
                description,
                accessToken,
                lang,
                idEnvironment,
                fileNameRegex,
                fileErrorMessage,
                idAccount,
                onError,
                dispatch,
            } = this.props;

            // fieldName is the name of the input field
            // file is the actual file object to send
            const formData = new FormData();

            if (idForm) {
                formData.append("idForm", idForm);
            }
            if (idFormField) {
                formData.append("idFormField", idFormField);
            }
            if (idActivity) {
                formData.append("idActivity", idActivity);
            }
            if (description) {
                formData.append("description", description);
            }
            formData.append("idAccount", idAccount);
            formData.append("_file", file, file.name);
            formData.append("channel", channel());
            formData.append("idEnvironment", idEnvironment);
            formData.append("lang", lang);

            const request = new XMLHttpRequest();

            dispatch(thirdPaymentActions.setLoading(true));
            await request.open("POST", `${window.API_URL}/files.upload`);

            await request.setRequestHeader("Authorization", `bearer ${accessToken}`);
            // Should call the progress method to update the progress to 100% before calling load
            // Setting computable to false switches the loading indicator to infinite mode
            request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total);
            };

            const patternString = fileNameRegex.source; // Obtiene la parte del patrón
            const flags = fileNameRegex.flags; // Obtiene los flags (en este caso "g")

            // Creamos una nueva RegExp dinámicamente
            const dynamicRegex = new RegExp(patternString, flags);

            if (isRegExp(dynamicRegex)) {
                const isValidaName = dynamicRegex.test(file.name);
                if (!isValidaName) {
                    onError({ errorMessage: i18n.get(fileErrorMessage) });
                    abort();
                    dispatch(thirdPaymentActions.setLoading(false));
                    return;
                }
            }

            // Should call the load method when done and pass the returned server file id
            // this server file id is then used later on when reverting or restoring a file
            // so your server knows which file to return without exposing that info to the client
            request.onload = () => {
                dispatch(thirdPaymentActions.setLoading(false));
                const { successNotificationMessage, warningNotificationMessage } = this.props;
                let errorMessage = i18n.get("forms.notification.uploadFile.fail");

                if (request.status >= 200 && request.status < 300) {
                    const response = JSON.parse(request.responseText);

                    if (response.code.endsWith("I")) {
                        const { onFileProcess } = this.props;
                        const { idFile, validLines, invalidLines, invalidFile, ...data } = response.data;

                        const uploadFileSuccessMessage = i18n.get("forms.notification.uploadFile.success");
                        const validLinesMessage = i18n.get("forms.notification.uploadFile.success.validLines");
                        const invalidLinesMessage = i18n.get("forms.notification.uploadFile.success.invalidLines");
                        // eslint-disable-next-line max-len
                        let message = `${uploadFileSuccessMessage}. ${validLinesMessage}: ${validLines}, ${invalidLinesMessage}: ${invalidLines}`;
                        load(response.data.idFile);

                        if (invalidLines === 0 && successNotificationMessage) {
                            message = successNotificationMessage;
                        }

                        const totalLines = invalidLines + validLines;
                        const percentage = (invalidLines / totalLines) * 100;
                        const minValidPercentage = configUtils.getInteger(
                            "forms.fields.multilinefile.minimum.invalid.percentage.allowed",
                        );
                        const isValidPercentage = percentage > minValidPercentage;

                        if (invalidLines !== 0 && warningNotificationMessage && !isValidPercentage) {
                            const obj = {
                                withParams: true,
                                id: warningNotificationMessage,
                                params: { validLines, totalLines },
                            };
                            message = JSON.stringify(obj);
                        }

                        if (isValidPercentage) {
                            const obj = {
                                withParams: true,
                                id: "forms.inputFile.massivePayments.invalid.file",
                                params: { percentage },
                            };
                            message = JSON.stringify(obj);
                        }

                        if (onFileProcess) {
                            onFileProcess({ idFile, message, validLines, invalidLines, ...data });
                        }
                    } else {
                        if (onError) {
                            // // se personaliza este mensaje para el poryecto de echeq massivo
                            if (response.code === "COR020W") {
                                errorMessage =
                                    response.data.message || i18n.get("forms.inputFile.massivePayments.valid.count");

                                onError({ errorMessage, ...response.data });
                            } else {
                                onError({ errorMessage, ...response.data });
                            }
                        }
                        error("oh no");
                    }
                } else {
                    // Can call the error method if something is wrong, should exit after
                    if (onError) {
                        onError({ errorMessage });
                    }
                    error("oh no");
                }
            };

            request.send(formData);

            // Should expose an abort method so the request can be cancelled
            // eslint-disable-next-line consistent-return
            return {
                abort: () => {
                    // This function is entered if the user has tapped the cancel button
                    request.abort();
                    dispatch(thirdPaymentActions.setLoading(false));

                    // Let FilePond know the request has been cancelled
                    abort();
                },
            };
        },
        revert: (uniqueFileId, load) => {
            // Should remove the earlier created temp file here
            if (uniqueFileId) {
                this.onRemoveFile({ serverId: uniqueFileId });
            }
            load();
        },
    });

    render() {
        const {
            idForm,
            idFormField,
            idActivity,
            description,
            accessToken,
            files,
            acceptedFileTypes,
            notificationScope,
            dispatch,
            allowFileTypeValidation,
            ...rest
        } = this.props;
        let labels = null;

        labels = this.getLabels();
        const serverOptions = this.getServerOptions();
        const invalidFileErrorMessage = "forms.inputFile.massivePayments.invalid.file.type";

        return (
            <FilePond
                ref={(ref) => {
                    this.pond = ref;
                }}
                server={serverOptions}
                {...labels}
                {...rest}
                acceptedFileTypes={acceptedFileTypes}
                onprocessfile={this.onProcessFile}
                beforeRemoveFile={this.onRemoveFile}
                allowFileTypeValidation={allowFileTypeValidation}
                onupdatefiles={(fileItems) => {
                    const error = fileItems.some((fileItem) => !acceptedFileTypes?.includes(fileItem.fileType));
                    if (error) {
                        dispatch(
                            notificationActions.showNotification(invalidFileErrorMessage, "error", [notificationScope]),
                        );
                    }
                }}
                allowRetry={false}>
                {/* Update current files  */}
                {files &&
                    files.map((fileItem) => (
                        <File
                            key={fileItem.fileId}
                            src={fileItem.fileId}
                            origin="local"
                            name={fileItem.fileName}
                            size={fileItem.fileSize}
                            type={fileItem.fileType}
                        />
                    ))}
            </FilePond>
        );
    }
}

const mapStateToProps = (state) => ({
    accessToken: sessionSelectors.getAccessToken(state),
    idRelatedFile: fileSelectors.getIdRelatedFile(state),
    lang: i18nSelectors.getLang(state),
    idEnvironment: sessionSelectors.getActiveEnvironment(state).id,
});

export default connect(mapStateToProps)(FileUploader);
